import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ChildSetupForm from '../components/ChildSetupForm';
import ChildSwitcher from '../components/ChildSwitcher';
import PinPanel from '../components/PinPanel';
import SummaryCards from '../components/SummaryCards';
import SessionHistory from '../components/SessionHistory';
import CurriculumAssignments from '../components/CurriculumAssignments';
import {
  childDisplayName
} from '../utils/parentDashboardData';
import { createChildWithAssignments } from '../lib/createChild';
import {
  fetchAllChildrenForCurrentUser,
  fetchCurriculumAndSessionsForChild
} from '../lib/curriculum';
import {
  invokeEdgeFunction,
  minutesUntil
} from '../lib/parentSession';
import { supabase } from '../lib/supabaseClient';

const {
  FiTrendingUp,
  FiInfo,
  FiLock,
  FiUserCheck,
  FiUserPlus,
  FiKey,
  FiShield,
  FiUserX
} = FiIcons;

function initialsFromName(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  return '?';
}

function toSwitcherChild(child) {
  return {
    id: child.id,
    name: child.name,
    grade: child.grade,
    initials: initialsFromName(child.name)
  };
}

function ParentDashboard({ householdId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [sessionCount, setSessionCount] = useState(0);
  const [switchingChild, setSwitchingChild] = useState(false);

  const [panel, setPanel] = useState(null);
  const [pin, setPin] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinNotice, setPinNotice] = useState('');
  const [addChildBusy, setAddChildBusy] = useState(false);
  const [addChildError, setAddChildError] = useState('');
  const [switchTargetId, setSwitchTargetId] = useState(null);
  const [archiveConfirmName, setArchiveConfirmName] = useState('');
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  const applyChildData = (nextChild, nextCurriculum, sessions) => {
    setChild(nextChild);
    setCurriculum(nextCurriculum);
    setSessionCount(sessions.length);
  };

  const loadChildIntoDashboard = async (nextChild) => {
    const { curriculum: nextCurriculum, sessions } =
      await fetchCurriculumAndSessionsForChild(nextChild.id);
    applyChildData(nextChild, nextCurriculum, sessions);
  };

  const refreshChildren = async (preferredChildId = null) => {
    // fetchAllChildrenForCurrentUser already excludes archived_at IS NOT NULL.
    const active = await fetchAllChildrenForCurrentUser();
    setChildren(active);

    if (active.length === 0) {
      applyChildData(null, [], []);
      return active;
    }

    const preferred = preferredChildId
      ? active.find((entry) => entry.id === preferredChildId)
      : null;
    const selected = preferred
      || active.find((entry) => entry.id === child?.id)
      || active[0];

    await loadChildIntoDashboard(selected);
    return active;
  };

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    refreshChildren()
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Could not load parent dashboard.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Initial load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closePanel = () => {
    setPanel(null);
    setPin('');
    setPinError('');
    setPinNotice('');
    setPinBusy(false);
    setAddChildBusy(false);
    setAddChildError('');
    setSwitchTargetId(null);
    setArchiveConfirmName('');
    setArchiveBusy(false);
    setArchiveError('');
  };

  const openPanel = (next) => {
    setPanel(next);
    setPin('');
    setPinError('');
    setPinNotice('');
    setAddChildError('');
    setArchiveConfirmName('');
    setArchiveError('');
  };

  const handleSelectChild = async (childId) => {
    if (!childId || childId === child?.id || switchingChild) return;
    const next = children.find((entry) => entry.id === childId);
    if (!next) return;

    setSwitchingChild(true);
    setError(null);
    try {
      await loadChildIntoDashboard(next);
      closePanel();
    } catch (caught) {
      setError(caught.message || 'Could not load this child’s data.');
    } finally {
      setSwitchingChild(false);
    }
  };

  const handleAddChild = async ({ name, grade }) => {
    if (!householdId) {
      setAddChildError('No household found for this account.');
      return;
    }

    setAddChildBusy(true);
    setAddChildError('');

    try {
      const newChild = await createChildWithAssignments({
        householdId,
        name,
        grade
      });
      await refreshChildren(newChild.id);
      closePanel();
    } catch (caught) {
      setAddChildError(caught.message || 'Could not add child.');
      setAddChildBusy(false);
    }
  };

  const handleOpenSwitchToStudent = () => {
    if (children.length > 1) {
      setSwitchTargetId(null);
      openPanel('switch-pick');
      return;
    }

    setSwitchTargetId(children[0]?.id || child?.id || null);
    openPanel('switch');
  };

  const handlePickSwitchChild = (childId) => {
    setSwitchTargetId(childId);
    setPin('');
    setPinError('');
    setPinNotice('');
    setPanel('switch');
  };

  const switchTargetChild = children.find((entry) => entry.id === switchTargetId)
    || child;
  const switchTargetName = childDisplayName(switchTargetChild);

  const handleSetStudentPin = async (event) => {
    event.preventDefault();
    if (!child?.id || pin.length !== 4) return;

    setPinBusy(true);
    setPinError('');
    setPinNotice('');

    const { data, error: invokeError } = await invokeEdgeFunction(
      'set-student-pin',
      { child_id: child.id, pin }
    );

    setPinBusy(false);

    if (invokeError || data?.success !== true) {
      setPinError(data?.error || invokeError || 'Could not save PIN.');
      return;
    }

    setPinNotice('PIN saved. You can switch to student mode now.');
    setPin('');
    setChild((current) => (
      current ? { ...current, auth_user_id: current.auth_user_id || 'set' } : current
    ));
    setChildren((current) => current.map((entry) => (
      entry.id === child.id
        ? { ...entry, auth_user_id: entry.auth_user_id || 'set' }
        : entry
    )));
  };

  const handleSetParentPin = async (event) => {
    event.preventDefault();
    if (!householdId || pin.length !== 4) return;

    setPinBusy(true);
    setPinError('');
    setPinNotice('');

    const { error: rpcError } = await supabase.rpc('set_household_pin', {
      target_household_id: householdId,
      new_pin: pin
    });

    setPinBusy(false);

    if (rpcError) {
      const message = rpcError.message || 'Could not save parent PIN.';
      if (message.includes('4 digits') || rpcError.code === '22023') {
        setPinError('PIN must be exactly 4 digits.');
        return;
      }
      if (message.includes('do not have access') || rpcError.code === '42501') {
        setPinError('You do not have access to this household.');
        return;
      }
      setPinError(message);
      return;
    }

    setPinNotice('Parent PIN saved. Students can use it to switch back to parent mode.');
    setPin('');
  };

  const handleSwitchToStudent = async (event) => {
    event.preventDefault();
    const targetId = switchTargetId || child?.id;
    if (!targetId || pin.length !== 4) return;

    setPinBusy(true);
    setPinError('');
    setPinNotice('');

    const { data, error: invokeError } = await invokeEdgeFunction(
      'student-pin-login',
      { child_id: targetId, pin }
    );

    if (data?.success === true && data.access_token && data.refresh_token) {
      try {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token
        });
        if (sessionError) throw sessionError;
        navigate('/', { replace: true });
        return;
      } catch (caught) {
        setPinBusy(false);
        setPinError(caught.message || 'Could not switch to student session.');
        return;
      }
    }

    setPinBusy(false);

    if (data?.error === 'incorrect_pin') {
      const remaining = data.attempts_remaining;
      setPinError(
        typeof remaining === 'number'
          ? `Incorrect PIN. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
          : 'Incorrect PIN. Try again.'
      );
      setPin('');
      return;
    }

    if (data?.locked || data?.error === 'pin_locked') {
      const mins = minutesUntil(data.locked_until);
      setPinError(
        mins
          ? `Too many attempts. Try again in about ${mins} minute${mins === 1 ? '' : 's'}.`
          : 'Too many attempts. Try again later.'
      );
      setPin('');
      return;
    }

    if (data?.error === 'child_archived') {
      setPinError('This student has been removed. Choose another student.');
      setPin('');
      return;
    }

    if (data?.error === 'no_pin_set' || data?.error === 'no_student_account') {
      setPinError('Set a PIN for this child first, then try switching again.');
      if (targetId === child?.id) {
        setPanel('set-student-pin');
      }
      setPin('');
      return;
    }

    setPinError(data?.error || invokeError || 'Could not switch to student.');
  };

  const handleArchiveChild = async (event) => {
    event.preventDefault();
    if (!child?.id) return;
    if (archiveConfirmName !== child.name) return;

    setArchiveBusy(true);
    setArchiveError('');

    try {
      const { error: rpcError } = await supabase.rpc('archive_child', {
        target_child_id: child.id
      });
      if (rpcError) throw rpcError;

      const remaining = await refreshChildren();
      closePanel();
      if (remaining.length === 0) {
        setError(null);
      }
    } catch (caught) {
      setArchiveError(caught.message || 'Could not remove this student.');
      setArchiveBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="parent-page page-shell">
        <div className="dashboard-loading">
          <p>Loading learning activity…</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="parent-page page-shell">
        <div className="dashboard-loading">
          <p className="dashboard-error">{error}</p>
        </div>
      </main>
    );
  }

  const switcherChildren = children.map(toSwitcherChild);
  const name = childDisplayName(child);
  const archiveNameMatches = Boolean(child) && archiveConfirmName === child.name;

  return (
    <motion.main
      animate={{ opacity: 1, y: 0 }}
      className="parent-page page-shell"
      initial={{ opacity: 0, y: 6 }}
    >
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Parent overview</span>
          <h1>
            {child ? `${name}’s learning activity` : 'Your household'}
          </h1>
          <p>
            {child
              ? `A focused snapshot of ${name}’s topics and curriculum.`
              : 'Add a student to start tracking learning activity.'}
          </p>
        </div>

        <div className="dashboard-actions">
          <span className="trend-chip">
            <SafeIcon icon={FiTrendingUp} />
            {sessionCount > 0 ? 'Activity on record' : 'No activity yet'}
          </span>
          <button
            className="pin-btn pin-btn-ghost"
            onClick={() => openPanel('add-child')}
            type="button"
          >
            <SafeIcon icon={FiUserPlus} />
            Add Child
          </button>
          {child ? (
            <>
              <button
                className="pin-btn pin-btn-ghost"
                onClick={() => openPanel('set-student-pin')}
                type="button"
              >
                <SafeIcon icon={FiKey} />
                Set Student PIN
              </button>
              <button
                className="pin-btn pin-btn-ghost"
                onClick={() => openPanel('set-parent-pin')}
                type="button"
              >
                <SafeIcon icon={FiShield} />
                Set Parent PIN
              </button>
              <button
                className="pin-btn pin-btn-primary"
                onClick={handleOpenSwitchToStudent}
                type="button"
              >
                <SafeIcon icon={FiUserCheck} />
                Switch to Student
              </button>
            </>
          ) : null}
        </div>
      </div>

      {children.length > 0 ? (
        <div className="dashboard-child-bar">
          <ChildSwitcher
            children={switcherChildren}
            selectedChildId={child?.id}
            onSelectChild={handleSelectChild}
          />
          {child ? (
            <button
              className="pin-btn pin-btn-ghost remove-student-btn"
              onClick={() => openPanel('archive-child')}
              type="button"
            >
              <SafeIcon icon={FiUserX} />
              Remove Student
            </button>
          ) : null}
        </div>
      ) : null}

      {panel === 'add-child' ? (
        <section className="add-child-panel">
          <div className="pin-panel-copy">
            <strong>
              <SafeIcon icon={FiUserPlus} />
              {' '}
              Add a child
            </strong>
            <p>
              Add another learner to this household. Curriculum assignments are
              created automatically for the pilot subjects.
            </p>
          </div>
          <ChildSetupForm
            busy={addChildBusy}
            error={addChildError}
            submitBusyLabel="Adding…"
            submitLabel="Add child"
            onCancel={closePanel}
            onSubmit={handleAddChild}
          />
        </section>
      ) : null}

      {panel === 'archive-child' && child ? (
        <form className="archive-panel" onSubmit={handleArchiveChild}>
          <div className="pin-panel-copy">
            <strong>
              <SafeIcon icon={FiUserX} />
              {' '}
              Remove {name}?
            </strong>
            <p>
              This hides {name} from your dashboard and turns off their PIN
              login. Their learning history is preserved — this is not a
              permanent deletion.
            </p>
          </div>
          <label className="archive-confirm-field">
            <span>
              Type
              {' '}
              <strong>{child.name}</strong>
              {' '}
              to confirm
            </span>
            <input
              autoComplete="off"
              disabled={archiveBusy}
              onChange={(event) => setArchiveConfirmName(event.target.value)}
              placeholder={child.name}
              type="text"
              value={archiveConfirmName}
            />
          </label>
          {archiveError ? <p className="pin-panel-error">{archiveError}</p> : null}
          <div className="pin-panel-actions">
            <button
              className="pin-btn pin-btn-ghost"
              disabled={archiveBusy}
              onClick={closePanel}
              type="button"
            >
              Cancel
            </button>
            <button
              className="pin-btn pin-btn-danger"
              disabled={archiveBusy || !archiveNameMatches}
              type="submit"
            >
              {archiveBusy ? 'Removing…' : 'Remove student'}
            </button>
          </div>
        </form>
      ) : null}

      {panel === 'set-student-pin' && child ? (
        <PinPanel
          busy={pinBusy}
          error={pinError}
          notice={pinNotice}
          onCancel={closePanel}
          onPinChange={setPin}
          onSubmit={handleSetStudentPin}
          pin={pin}
          submitLabel="Save PIN"
          subtitle={`Choose a 4-digit PIN for ${name}. You’ll use it to switch into their tutor session.`}
          title={(
            <>
              <SafeIcon icon={FiLock} />
              {' '}
              Set student PIN
            </>
          )}
        />
      ) : null}

      {panel === 'set-parent-pin' ? (
        <PinPanel
          busy={pinBusy}
          error={pinError}
          notice={pinNotice}
          onCancel={closePanel}
          onPinChange={setPin}
          onSubmit={handleSetParentPin}
          pin={pin}
          submitLabel="Save PIN"
          subtitle="Choose a 4-digit PIN. Students will enter it to switch back to the parent dashboard."
          title={(
            <>
              <SafeIcon icon={FiShield} />
              {' '}
              Set parent PIN
            </>
          )}
        />
      ) : null}

      {panel === 'switch-pick' ? (
        <section className="switch-pick-panel">
          <div className="pin-panel-copy">
            <strong>
              <SafeIcon icon={FiUserCheck} />
              {' '}
              Switch to Student
            </strong>
            <p>Choose which student to enter the tutor as.</p>
          </div>
          <div className="switch-pick-options" role="list">
            {switcherChildren.map((entry) => (
              <button
                className="child-option switch-pick-option"
                key={entry.id}
                onClick={() => handlePickSwitchChild(entry.id)}
                type="button"
              >
                <span className="child-option-avatar">{entry.initials}</span>
                <span className="child-option-copy">
                  <strong>{entry.name}</strong>
                  <small>{entry.grade} grade</small>
                </span>
              </button>
            ))}
          </div>
          <div className="pin-panel-actions">
            <button
              className="pin-btn pin-btn-ghost"
              onClick={closePanel}
              type="button"
            >
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      {panel === 'switch' && switchTargetChild ? (
        <PinPanel
          busy={pinBusy}
          error={pinError}
          notice={pinNotice}
          onCancel={
            children.length > 1
              ? () => {
                setPin('');
                setPinError('');
                setPinNotice('');
                setPanel('switch-pick');
              }
              : closePanel
          }
          onPinChange={setPin}
          onSubmit={handleSwitchToStudent}
          pin={pin}
          submitLabel="Enter tutor"
          subtitle={`Enter ${switchTargetName}’s PIN to open the Student Tutor as their account.`}
          title={(
            <>
              <SafeIcon icon={FiUserCheck} />
              {' '}
              Switch to Student
            </>
          )}
        />
      ) : null}

      {child ? (
        <>
          <SummaryCards curriculum={curriculum} />
          <SessionHistory child={child} curriculum={curriculum} />
          <CurriculumAssignments child={child} curriculum={curriculum} />

          <aside className="parent-note">
            <SafeIcon icon={FiInfo} />
            <div>
              <strong>Live household data</strong>
              <p>
                Curriculum and progress come from your Supabase household.
                Assignment toggles are view-only for now.
              </p>
            </div>
          </aside>
        </>
      ) : (
        <section className="dashboard-empty">
          <p>No active students in this household yet.</p>
          <button
            className="pin-btn pin-btn-primary"
            onClick={() => openPanel('add-child')}
            type="button"
          >
            <SafeIcon icon={FiUserPlus} />
            Add Child
          </button>
        </section>
      )}
    </motion.main>
  );
}

export default ParentDashboard;
